import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VerificationSelfie = ({ onSelfieChange, initialSelfie = null }) => {
  const [selfie, setSelfie] = useState(initialSelfie);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    if (!file?.type?.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file?.size > 10485760) {
      alert('File size must be less than 10MB');
      return;
    }

    const selfieData = {
      id: `${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`,
      file,
      preview: URL.createObjectURL(file),
      name: file?.name,
      timestamp: new Date()?.toISOString()
    };
    setSelfie(selfieData);
    onSelfieChange(selfieData);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const file = e?.dataTransfer?.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleRemove = () => {
    setSelfie(null);
    onSelfieChange(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-3 mb-4">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          selfie ? 'bg-success text-success-foreground' : 'bg-primary/10 text-primary'
        }`}>
          <Icon name="User" size={20} strokeWidth={2} />
        </div>
        <div>
          <h3 className="font-heading text-lg font-semibold text-foreground">
            Verification Selfie
          </h3>
          <p className="font-body text-sm text-muted-foreground">
            Take a selfie holding your driver's license next to the trailer
          </p>
        </div>
      </div>

      {!selfie ? (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-smooth ${
            isDragging
              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
          }`}
        >
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Camera" size={32} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div>
              <p className="font-body text-base text-foreground mb-2">
                Capture Verification Photo
              </p>
              <Button
                variant="primary"
                onClick={() => fileInputRef?.current?.click()}
                iconName="Camera"
              >
                Take Selfie
              </Button>
            </div>
            <p className="font-caption text-xs text-muted-foreground">
              JPG, PNG, WebP (Max 10MB)
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="user"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      ) : (
        <div className="relative">
          <div className="aspect-[4/3] rounded-lg overflow-hidden bg-muted border-2 border-success">
            <img
              src={selfie?.preview}
              alt="Verification selfie"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute top-3 right-3 flex space-x-2">
            <button
              onClick={() => fileInputRef?.current?.click()}
              className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center hover:scale-110 transition-smooth shadow-elevation-2"
              aria-label="Retake photo"
            >
              <Icon name="RefreshCw" size={18} strokeWidth={2.5} />
            </button>
            <button
              onClick={handleRemove}
              className="w-10 h-10 bg-error text-error-foreground rounded-full flex items-center justify-center hover:scale-110 transition-smooth shadow-elevation-2"
              aria-label="Remove photo"
            >
              <Icon name="X" size={18} strokeWidth={2.5} />
            </button>
          </div>
          <div className="absolute bottom-3 left-3 right-3 bg-success/90 backdrop-blur-sm rounded px-3 py-2 flex items-center space-x-2">
            <Icon name="CheckCircle2" size={16} className="text-success-foreground" strokeWidth={2.5} />
            <p className="font-body text-sm text-success-foreground font-semibold">
              Verification photo captured
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="user"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      <div className="p-4 bg-warning/10 border border-warning/20 rounded-md">
        <div className="flex items-start space-x-3">
          <Icon name="AlertCircle" size={20} className="text-warning mt-0.5" strokeWidth={2} />
          <div className="flex-1">
            <p className="font-body text-sm text-foreground font-semibold mb-2">
              Important Requirements:
            </p>
            <ul className="font-body text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Hold your driver's license clearly visible in the photo</li>
              <li>Stand next to the trailer so both are in frame</li>
              <li>Ensure your face and license details are clearly visible</li>
              <li>Use good lighting for clear identification</li>
              <li>Photo will be used for pickup verification</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationSelfie;